import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  IndianRupee,
  Loader2,
  QrCode,
  ReceiptText,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import qrImage from "@/assets/images/qr-ss.jpg";

const SUBMIT_ENDPOINT = "/backend/api/manual/submit.php";

export default function Manual() {
  const navigate = useNavigate();

  const [trNo, setTrNo] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const numericAmount = Number(amount);
  const validAmount =
    Number.isFinite(numericAmount) && numericAmount >= 1;

  const handleSubmit = async (event) => {
    event.preventDefault();

    const cleanTrNo = trNo.replace(/\D/g, "");

    if (!/^\d{12,17}$/.test(cleanTrNo)) {
      toast.error("Enter a valid 12 to 17 digit transaction / UTR number.");
      return;
    }

    if (!validAmount) {
      toast.error("Enter a valid amount.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(SUBMIT_ENDPOINT, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          tr_no: cleanTrNo,
          amount: numericAmount,
        }),
      });

      const result = await response.json().catch(() => null);

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (!response.ok || !result?.success) {
        throw new Error(
          result?.message || "Unable to submit payment request."
        );
      }

      setSuccess({
        trNo: cleanTrNo,
        amount: result.amount || numericAmount.toFixed(2),
      });

      toast.success("Payment request submitted successfully.");
    } catch (error) {
      toast.error(error.message || "Unable to submit payment request.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-[calc(100vh-65px)] px-4 py-8 sm:px-6 sm:py-12">
        <div className="mx-auto max-w-xl">
          <Card className="rounded-3xl border border-border/70">
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" />

              <h1 className="mt-5 text-2xl font-display font-bold">
                Request Submitted
              </h1>

              <p className="mt-2 text-sm text-muted-foreground">
                Your manual payment request is pending verification.
                Your wallet will be updated after the payment is verified.
              </p>

              <div className="mt-6 rounded-2xl border bg-muted/30 p-4 text-left text-sm">
                <p>
                  <span className="font-semibold">Transaction / UTR:</span>{" "}
                  {success.trNo}
                </p>
                <p className="mt-2">
                  <span className="font-semibold">Amount:</span>{" "}
                  ₹ {success.amount}
                </p>
                <p className="mt-2">
                  <span className="font-semibold">Status:</span>{" "}
                  Pending
                </p>
              </div>

              <Button
                type="button"
                className="mt-6 w-full"
                onClick={() => navigate("/dashboard")}
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-[calc(100vh-65px)] overflow-hidden px-4 py-8 sm:px-6 sm:py-12">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-16 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-gradient-to-tr from-emerald-500/10 to-cyan-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-5xl">
        <button
          type="button"
          onClick={() => navigate("/wallet")}
          className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.05] hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Wallet
        </button>

        <Card className="overflow-hidden rounded-3xl border border-border/70">
          <CardContent className="p-0">
            <div className="border-b border-border/70 p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600">
                  <QrCode className="h-7 w-7" />
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Smart Solutions Wallet
                  </p>
                  <h1 className="mt-1 text-2xl font-display font-bold sm:text-3xl">
                    Manual UPI / Bank Deposit
                  </h1>
                  <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                    Pay using the QR code or bank details, then submit your
                    transaction number and amount for verification.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-0 lg:grid-cols-2">
              <div className="border-b border-border/70 p-6 sm:p-8 lg:border-b-0 lg:border-r">
                <div className="flex items-center gap-2">
                  <ReceiptText className="h-5 w-5 text-primary" />
                  <h2 className="font-display text-lg font-bold">
                    Submit payment details
                  </h2>
                </div>

                <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="tr_no">
                      Transaction / UTR Number
                    </Label>
                    <Input
                      id="tr_no"
                      value={trNo}
                      onChange={(e) =>
                        setTrNo(e.target.value.replace(/\D/g, ""))
                      }
                      inputMode="numeric"
                      maxLength={17}
                      placeholder="Enter UTR / transaction number"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter the 12 to 17 digit reference shown by your
                      bank or UPI app.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount</Label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="amount"
                        type="number"
                        min="1"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="pl-9"
                        placeholder="Enter amount paid"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || !validAmount}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit for Verification"
                    )}
                  </Button>
                </form>

                <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <p className="text-xs leading-5 text-muted-foreground">
                    Submit only after completing the payment. Manual requests
                    remain pending until Smart Solutions verifies the
                    transaction.
                  </p>
                </div>
              </div>

              <div className="p-6 sm:p-8">
                <h2 className="font-display text-lg font-bold">
                  Payment Details
                </h2>

<div className="mt-5 mx-auto flex h-[200px] w-[200px] items-center justify-center p-2">
  <img
    src={qrImage}
    alt="Smart Solutions payment QR"
    className="max-h-[180px] max-w-[180px] object-contain"
  />
</div>

                <div className="mt-5 rounded-2xl border border-border/70 bg-background/40 p-5">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">Bank Transfer</h3>
                  </div>

                  <dl className="mt-4 space-y-3 text-sm">
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Account Number</dt>
                      <dd className="font-semibold">39149586790</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">IFSC Code</dt>
                      <dd className="font-semibold">SBIN0000418</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">Account Name</dt>
                      <dd className="font-semibold">Smart Solutions</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
